import { Grid, GridColumn as Column, GridCustomCellProps, GridDataStateChangeEvent } from '@progress/kendo-react-grid';
import { circleIcon } from '@progress/kendo-svg-icons';
import { useNavigate } from 'react-router-dom';
import { ClientRoutes } from '../../../../../Constants/ClientRoutingConstants';
import { SvgIcon } from '@progress/kendo-react-common';
import { enMessages } from '../../../../../messages/en-US';
import { useLocalization } from '@progress/kendo-react-intl';
import { useState } from 'react';
import { State, process } from '@progress/kendo-data-query';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestion } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { GetHeadUnitIcon } from "../../../../../Utilities/CustomIcons";
import { formatSmartDate } from "../../../../Shared/Utilities/DateFormattingUtils";

interface Props {
  healthData: any;
}

export default function HeadUnitsHealthTable({ healthData }: Props) {
  const navigate = useNavigate();
  const localizationService = useLocalization();
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Healthy':
      case 'Online':
        return 'text-gbBrand-green-700';
      case 'Unhealthy':
      case 'Offline':
        return 'text-gbBrand-red-500';
      case 'Warning':
        return 'text-gbBrand-yellow-500';
      default:
        return 'text-gbBrand-blue-500';
    }
  };

  const ExpectedTasksCell = (props: any) => {
    return <td>
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="large" className={getStatusColor(props.dataItem.expectedTasks.status)} />
        <span>{props.dataItem.expectedTasks.value}/{props.dataItem.expectedTasks.count}</span>
      </div>
    </td>;
  };

  const ConnectivityCell = (props: any) => {
    return <td>
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="large" className={getStatusColor(props.dataItem.connectivityStatus)} />
        <span>{props.dataItem.connectivityStatus}</span>
      </div>
    </td>;
  };

  const LastActivityCell = (props: GridCustomCellProps) => {
    const localizationService = useLocalization();
    const { displayText: lastSeenText, tooltipText } = formatSmartDate(
      props.dataItem.lastActivity,
      localizationService,
      enMessages
    );

    return <td title={tooltipText}>
      {lastSeenText}
    </td>;
  };

  const TypeCell = (props: GridCustomCellProps) => {
    let tooltipText = "";
    if (props.dataItem.deviceType === "HeadUnit") {
      tooltipText = `Head Unit`;
    } else {
      tooltipText = localizationService.toLanguageString('custom.unknownType', enMessages.custom.unknownType);
    }

    return (
      <td style={{padding: "0px"}}>
        <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
              cursor: "default",
            }}
            title={tooltipText}
          >
            {props.dataItem.deviceType === "HeadUnit" ? (
              GetHeadUnitIcon(26, 26)
            ) : (
              <FontAwesomeIcon icon={faQuestion} size="lg" />
            )}
          </span>
        </Tooltip>
      </td>
    );
  };

  const FirmwareVersionCell = (props: any) => {
    return <td>{props.dataItem.firmwareVersion !== '' ? props.dataItem.firmwareVersion : '-'}</td>;
  };

  const SerialNumberCell = (props: any) => {
    return <td>
      <button 
        className="text-left text-blue-600 underline cursor-pointer"
        onClick={() => navigate(`${ClientRoutes.HealthDashboardPage}/${props.dataItem.id}`, { 
          state: { 
            deviceData: props.dataItem, 
            deviceType: "headUnit",
            selectedTile: "headUnits"
          } 
        })}
      >
        {props.dataItem.serialNumber}
      </button>
    </td>;
  };

  // Initialize data state for virtual scrolling
  const [dataState, setDataState] = useState<State>({
    skip: 0,
    take: 30,
    sort: [{ field: 'connectivityStatus', dir: 'asc' }],
    filter: {
      logic: 'and',
      filters: []
    }
  });

  // Handle data state changes (pagination, sorting, filtering)
  const dataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  };

  // Process the data with current state
  const result = process(healthData, dataState);

  return (
    <>
      <div className="text-xl font-bold p-4">{localizationService.toLanguageString('custom.headUnitsHealth', enMessages.custom.headUnitsHealth)}</div>
      <Grid
        style={{ height: 'calc(100vh - 345px)', margin: '0 10px 10px 10px' }}
        rowHeight={46}
        data={result.data}
        total={result.total}
        skip={dataState.skip}
        take={dataState.take}
        scrollable="virtual"
        sortable={true}
        filterable={true}
        onDataStateChange={dataStateChange}
        {...dataState}
      >
        <Column field="deviceType" title=" " cells={{data: TypeCell}} width="45px" filterable={false} />
        <Column field="serialNumber" title={localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber)} cells={{data: SerialNumberCell}} />
        <Column field="connectivityStatus" title={localizationService.toLanguageString('custom.connectivity', enMessages.custom.connectivity)} cells={{data: ConnectivityCell}} />
        <Column field="expectedTasks" title={localizationService.toLanguageString('custom.expectedTasks', enMessages.custom.expectedTasks)} cells={{data: ExpectedTasksCell}} />
        <Column field="inactivityTimeoutInMinutes" title={localizationService.toLanguageString('custom.inactivityTimeoutInMinutes', enMessages.custom.inactivityTimeoutInMinutes)} />
        <Column field="lastActivity" title={localizationService.toLanguageString('custom.lastActivity', enMessages.custom.lastActivity)} cells={{data: LastActivityCell}} />
        <Column field="firmwareVersion" title={localizationService.toLanguageString('custom.firmwareVersion', enMessages.custom.firmwareVersion)} cells={{data: FirmwareVersionCell}} />
      </Grid>
    </>
  );
}
